// src/components/Dashboard/PortfolioDashboard.js
//
// Phase 2 (2026-05-14) - global Dashboard sidebar entry. Open to both
// Pentester and Wasabian. Shows portfolio KPIs, grade-distribution donut,
// risk-over-time line (driven by pt_score_snapshots), and the top-10
// at-risk vaults (most open criticals first).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowRight, BarChart3, Briefcase,
  Bug, Loader2, Radio, Server, TrendingDown, Wand2,
} from 'lucide-react';
import { adminFindingsAPI, monitorAPI, portfolioDashboardAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { GradeDonut, RiskOverTimeLine } from '../common/Charts';
import { HeroStrip, PrimaryCTA } from '../common/Shinobi';

const GRADE_COLOR = {
  A: 'text-emerald-700',
  B: 'text-emerald-600',
  C: 'text-yellow-700',
  D: 'text-orange-700',
  F: 'text-red-700',
};

// Glow theme per accent: gradient bg, border, text, halo, and a soft outer
// shadow that gives each tile a "lit" feel. The accent on the right edge
// is a thin vertical bar that pulses subtly.
// Clean KPI tile: white card, solid colored left accent bar, filled icon chip,
// bold accent number. Defined borders + subtle shadow (no faint gradient wash).
const KPI_THEME = {
  green: { text: 'text-wasabi-700', border: 'border-wasabi-200', card: 'bg-wasabi-50', iconBg: 'bg-wasabi-100', iconText: 'text-wasabi-600', bar: 'bg-wasabi-500' },
  red:   { text: 'text-rose-700',   border: 'border-rose-200',   card: 'bg-rose-50',   iconBg: 'bg-rose-100',   iconText: 'text-rose-600',   bar: 'bg-rose-500' },
  amber: { text: 'text-amber-700',  border: 'border-amber-200',  card: 'bg-amber-50',  iconBg: 'bg-amber-100',  iconText: 'text-amber-600',  bar: 'bg-amber-500' },
  slate: { text: 'text-slate-800',  border: 'border-slate-200',  card: 'bg-white',     iconBg: 'bg-slate-100',  iconText: 'text-slate-600',  bar: 'bg-slate-400' },
};

function KPIBox({ label, value, sublabel, icon: Icon, accent = 'green' }) {
  const t = KPI_THEME[accent] || KPI_THEME.green;
  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${t.border} ${t.card} p-4 pl-5 shadow-card transition-shadow duration-200 hover:shadow-card-hover`}
    >
      {/* Solid accent bar on the left edge */}
      <span className={`absolute top-0 bottom-0 left-0 w-1 ${t.bar}`} />
      <div className="flex items-center gap-2 mb-2">
        {Icon && (
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${t.iconBg}`}>
            <Icon className={`w-4 h-4 ${t.iconText}`} />
          </span>
        )}
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
          {label}
        </span>
      </div>
      <div className={`text-4xl font-bold tabular-nums ${t.text}`}>
        {value}
      </div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}

function ChartCard({ icon: Icon, title, accent = 'green', rightLabel, empty, emptyMsg, children }) {
  const accentMap = {
    green: { bar: 'bg-shinobi-green', halo: 'bg-wasabi-100', border: 'border-wasabi-300', text: 'text-shinobi-green' },
    rose:  { bar: 'bg-rose-400',      halo: 'bg-rose-100',     border: 'border-rose-300',     text: 'text-rose-700' },
  };
  const t = accentMap[accent] || accentMap.green;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-4 transition-all duration-300 hover:border-wasabi-300 hover:shadow-[0_0_28px_-10px_rgba(0,206,62,0.4)]">
      <span className={`pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-50 ${t.halo}`} />
      <span className={`absolute top-0 left-0 right-0 h-[2px] ${t.bar} opacity-70`} />
      <div className="relative flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border ${t.border} bg-cyber-100/40`}>
              <Icon className={`w-3.5 h-3.5 ${t.text}`} />
            </span>
          )}
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        </div>
        {rightLabel && (
          <span className="text-[10px] uppercase tracking-wider text-slate-500">{rightLabel}</span>
        )}
      </div>
      {empty ? (
        <div className="relative text-sm text-slate-500 italic text-center py-12">{emptyMsg}</div>
      ) : (
        <div className="relative">{children}</div>
      )}
    </div>
  );
}

function ClassifyAllPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enqueueing, setEnqueueing] = useState(false);
  const [message, setMessage] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const s = await adminFindingsAPI.classifyStatus();
      setStatus(s);
    } catch (err) {
      // Swallow: the panel just hides itself when status is unavailable.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return null;
  if (!status) return null;
  if (status.null_count === 0) return null;

  const onClassify = async () => {
    setEnqueueing(true);
    setMessage(null);
    try {
      const r = await adminFindingsAPI.classifyAll();
      setMessage(`Classification job enqueued for ${r.null_count_before} finding${r.null_count_before === 1 ? '' : 's'}.`);
      // Re-check after a short delay so the operator sees progress.
      setTimeout(refresh, 2000);
    } catch (err) {
      setMessage(`Error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setEnqueueing(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-500/5 p-4 flex items-start gap-3">
      <Wand2 className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-amber-700">
          {status.null_count} finding{status.null_count === 1 ? '' : 's'} not yet categorised
        </div>
        <p className="text-xs text-amber-700/80 mt-1">
          Wasabi Shinobi AI classifies each finding into one of 5 categories for the spider chart.
          The catch-up job runs every 15 minutes; click below to backfill immediately.
        </p>
        {message && <div className="text-xs text-amber-700/80 mt-2">{message}</div>}
        <div className="mt-3">
          <PrimaryCTA
            variant="green"
            icon={Wand2}
            iconPosition="left"
            loading={enqueueing}
            disabled={!status.ai_available}
            onClick={onClassify}
          >
            Classify all now
          </PrimaryCTA>
        </div>
        {!status.ai_available && (
          <div className="text-xs text-amber-700/80 mt-2">
            AI service not configured on the server; categories will stay NULL until that's set.
          </div>
        )}
      </div>
    </div>
  );
}

// Network monitor KPI band. Shown to every role (Wasabian + Blue Teamer get a
// monitor-focused dashboard; Red Teamer sees it below the PT portfolio).
function MonitorBand({ monitorKpis, navigate }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h2 className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Radio className="w-3.5 h-3.5 text-shinobi-green" />
          Network monitor
        </h2>
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <button onClick={() => navigate('/monitor/assets')} className="hover:text-shinobi-purple transition-colors">
            Asset Inventory →
          </button>
          <button onClick={() => navigate('/monitor/findings')} className="hover:text-shinobi-purple transition-colors">
            Findings →
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPIBox label="Discovered assets" value={monitorKpis.total_assets} icon={Server} accent="slate" />
        <KPIBox
          label="Open findings"
          value={monitorKpis.total_open_findings}
          sublabel={`${monitorKpis.critical_count} critical · ${monitorKpis.high_count} high`}
          icon={Bug}
          accent="amber"
        />
        <KPIBox label="Critical findings" value={monitorKpis.critical_count} sublabel="Open + critical" icon={AlertTriangle} accent="red" />
        <KPIBox label="Monitor beacons" value={monitorKpis.monitor_beacons_online} sublabel="Online now" icon={Radio} accent="green" />
      </div>
    </section>
  );
}

export default function PortfolioDashboard() {
  const navigate = useNavigate();
  const { isPentester, canViewOffensive } = useAuth();
  const [data, setData] = useState(null);
  const [monitorKpis, setMonitorKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    // Blue Teamers have no offensive/portfolio access; skip the PT fetch (it
    // would 403) and show a monitor-focused dashboard instead.
    if (!canViewOffensive) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await portfolioDashboardAPI.get();
      setData(resp);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [canViewOffensive]);

  const fetchMonitorKpis = useCallback(async () => {
    // Monitor KPIs are readable by every role (the monitor dashboard endpoint
    // is open to all authenticated users).
    try {
      const resp = await monitorAPI.dashboard();
      setMonitorKpis(resp.data);
    } catch (err) {
      // Soft-fail: the network monitor band is additive; failure should not
      // break the dashboard. Log to console for debugging.
      console.warn('monitor dashboard fetch failed:', err?.response?.data?.detail || err.message);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { fetchMonitorKpis(); }, [fetchMonitorKpis]);

  const gradeDonutData = useMemo(() => {
    if (!data?.grade_distribution) return [];
    return ['A', 'B', 'C', 'D', 'F']
      .map((g) => ({ name: g, value: data.grade_distribution[g] || 0 }))
      .filter((d) => d.value > 0);
  }, [data]);

  return (
    <div className="space-y-6">
      <HeroStrip
        icon={BarChart3}
        accent="green"
        eyebrow={canViewOffensive ? 'Portfolio' : 'Monitoring'}
        title={canViewOffensive ? 'Wasabi Red Team Dashboard' : 'Network Monitor Dashboard'}
        subtitle={canViewOffensive
          ? 'Cross-engagement KPIs, grade distribution, risk trend, and the vaults that need attention first.'
          : 'Discovered assets, open vulnerability findings, and monitor beacon status across your network.'}
      />

      <div className="px-6 space-y-6">
        {loading ? (
          <div className="text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading dashboard...
          </div>
        ) : !canViewOffensive ? (
          // Blue Teamer: monitor-focused dashboard (no offensive/portfolio data).
          monitorKpis ? (
            <MonitorBand monitorKpis={monitorKpis} navigate={navigate} />
          ) : (
            <div className="text-slate-500 text-sm">
              No monitor data yet. Deploy a monitor beacon (Internal Scanner) to populate
              Asset Inventory and Vulnerability Findings.
            </div>
          )
        ) : error ? (
          <div className="text-red-600 text-sm">Error: {error}</div>
        ) : data ? (
          <>
            {isPentester && <ClassifyAllPanel />}

            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <KPIBox
                label="Penetration tests"
                value={data.kpis.total_pts}
                sublabel="Non-archived"
                icon={Briefcase}
                accent="slate"
              />
              <KPIBox
                label="Open criticals"
                value={data.kpis.open_criticals}
                sublabel="Across portfolio"
                icon={AlertTriangle}
                accent="red"
              />
              <KPIBox
                label="Vaults at risk"
                value={data.kpis.vaults_at_risk}
                sublabel="Grade D or F"
                icon={TrendingDown}
                accent="amber"
              />
              <KPIBox
                label="Average score"
                value={Number(data.kpis.avg_score).toFixed(2)}
                sublabel="/ 5.00"
                icon={Activity}
                accent="green"
              />
            </section>

            {monitorKpis && <MonitorBand monitorKpis={monitorKpis} navigate={navigate} />}

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard
                icon={Activity}
                title="Grade distribution"
                accent="green"
                empty={gradeDonutData.length === 0}
                emptyMsg="No active engagements."
              >
                <GradeDonut data={gradeDonutData} />
              </ChartCard>

              <ChartCard
                icon={TrendingDown}
                title="Risk over time"
                accent="rose"
                rightLabel={`Open criticals · last ${data.risk_over_time.length} day${data.risk_over_time.length === 1 ? '' : 's'}`}
                empty={data.risk_over_time.length === 0}
                emptyMsg="No snapshots yet. The daily snapshot job runs at 02:10 UTC; first data point arrives tomorrow."
              >
                <RiskOverTimeLine data={data.risk_over_time} />
              </ChartCard>
            </section>

            <section className="relative overflow-hidden rounded-2xl border border-wasabi-300 bg-gradient-to-br from-shinobi-green/8 via-cyber-50/60 to-rose-500/5 p-5">
              <span className="pointer-events-none absolute -top-20 -left-20 w-64 h-64 rounded-full bg-wasabi-100 blur-3xl" />
              <span className="pointer-events-none absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-rose-100 blur-3xl" />
              <div className="relative flex items-center gap-2 mb-4">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-wasabi-300 bg-wasabi-100">
                  <AlertTriangle className="w-4 h-4 text-shinobi-green" />
                </span>
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.16em]">Top vaults to watch</h3>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-auto">
                  Most open criticals first
                </span>
              </div>
              {data.top_10_vaults.length === 0 ? (
                <div className="relative text-sm text-slate-500 italic">No engagements yet.</div>
              ) : (
                <ul className="relative space-y-1.5">
                  {data.top_10_vaults.map((v, idx) => {
                    const cls = GRADE_COLOR[v.grade] || 'text-slate-700';
                    const danger = v.open_criticals > 0;
                    return (
                      <li key={v.id}>
                        <button
                          type="button"
                          onClick={() => navigate(`/pentests/${v.id}/dashboards`)}
                          className={`w-full text-left px-3 py-2.5 flex items-center gap-3 rounded-xl border transition-all duration-200
                            border-cyber-300/30 bg-cyber-100/40 hover:border-wasabi-300 hover:bg-cyber-200/30
                            hover:shadow-[0_0_18px_-6px_rgba(0,206,62,0.4)]
                            group`}
                        >
                          <span className="text-[10px] font-mono text-slate-500 w-6">#{idx + 1}</span>
                          <span className={`relative inline-flex items-center justify-center w-9 h-9 rounded-lg border ${cls.replace('text-', 'border-').replace('-300', '-400/50').replace('-400', '-400/50')} bg-cyber-100/60 text-base font-bold ${cls}`}>
                            {v.grade}
                          </span>
                          <span className="flex-1 text-sm text-slate-800 truncate group-hover:text-wasabi-600 transition">
                            {v.name}
                          </span>
                          <span className="font-mono text-xs text-slate-500">{Number(v.overall_score).toFixed(2)}</span>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${
                              danger
                                ? 'bg-rose-100 text-rose-700 border-rose-300 shadow-[0_0_12px_-3px_rgba(248,113,113,0.5)]'
                                : 'bg-wasabi-100 text-wasabi-700 border-wasabi-300'
                            }`}
                          >
                            {danger && <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />}
                            {v.open_criticals} critical
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-shinobi-green group-hover:translate-x-0.5 transition shrink-0" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}
